import React from 'react';
import { Box, Flex, Icon, SimpleGrid, Spinner, Text, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  MdBusiness,
  MdCheckCircle,
  MdLocationOn,
  MdLocationCity,
  MdSubscriptions,
  MdSchedule,
  MdPauseCircle,
  MdPeople,
  MdPlayCircle,
  MdDateRange,
  MdLibraryMusic,
  MdWarning,
} from 'react-icons/md';
import { apiClient } from '../../services/apiClient';
import MiniStatistics from '../../components/card/MiniStatistics';
import { TrendCharts } from './TrendCharts';

interface DashboardMetrics {
  totalOrganizations: number;
  activeOrganizations: number;
  totalLocations: number;
  activeLocations: number;
  totalUsers: number;
  activeSubscriptions: number;
  trialSubscriptions: number;
  inactiveSubscriptions: number;
  libraryTrackCount: number;
  playbackHoursToday: number;
  playbackHoursWeek: number;
  errorRate: number | null;
}

function IconBox({ icon, bg }: { icon: React.ElementType; bg: string }) {
  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      borderRadius="50%"
      w="56px"
      h="56px"
      bg={bg}
    >
      <Icon as={icon} w="28px" h="28px" color="white" />
    </Flex>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'secondaryGray.500');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-metrics-dashboard'],
    queryFn: () => apiClient.get<DashboardMetrics>('/admin/metrics/dashboard'),
    refetchInterval: 60 * 1000,
  });

  if (isLoading) return <Flex justify="center" p={8}><Spinner size="xl" /></Flex>;

  const errorRateValue =
    data?.errorRate != null
      ? `${(data.errorRate * 100).toFixed(2)}%`
      : t('admin.dashboard.notAvailable');

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="dashboard-page">
      {/* Organizations & Locations */}
      <Text color={textColorSecondary} fontSize="md" fontWeight="600" mb="8px">
        {t('admin.dashboard.organizations')}
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2, '2xl': 4 }} gap="20px" mb="20px">
        <MiniStatistics
          startContent={<IconBox icon={MdBusiness} bg="brand.500" />}
          name={t('admin.dashboard.totalOrganizations')}
          value={data?.totalOrganizations ?? 0}
        />
        <MiniStatistics
          startContent={<IconBox icon={MdCheckCircle} bg="green.500" />}
          name={t('admin.dashboard.activeOrganizations')}
          value={data?.activeOrganizations ?? 0}
        />
        <MiniStatistics
          startContent={<IconBox icon={MdLocationOn} bg="brand.500" />}
          name={t('admin.dashboard.totalLocations')}
          value={data?.totalLocations ?? 0}
        />
        <MiniStatistics
          startContent={<IconBox icon={MdLocationCity} bg="green.500" />}
          name={t('admin.dashboard.activeLocations')}
          value={data?.activeLocations ?? 0}
        />
      </SimpleGrid>

      {/* Subscriptions */}
      <Text color={textColorSecondary} fontSize="md" fontWeight="600" mb="8px">
        {t('admin.dashboard.subscriptions')}
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2, '2xl': 4 }} gap="20px" mb="20px">
        <MiniStatistics
          startContent={<IconBox icon={MdSubscriptions} bg="brand.500" />}
          name={t('admin.dashboard.activeSubscriptions')}
          value={data?.activeSubscriptions ?? 0}
        />
        <MiniStatistics
          startContent={<IconBox icon={MdSchedule} bg="blue.500" />}
          name={t('admin.dashboard.trialSubscriptions')}
          value={data?.trialSubscriptions ?? 0}
        />
        <MiniStatistics
          startContent={<IconBox icon={MdPauseCircle} bg="orange.500" />}
          name={t('admin.dashboard.inactiveSubscriptions')}
          value={data?.inactiveSubscriptions ?? 0}
        />
        <MiniStatistics
          startContent={<IconBox icon={MdPeople} bg="brand.500" />}
          name={t('admin.dashboard.totalUsers')}
          value={data?.totalUsers ?? 0}
        />
      </SimpleGrid>

      {/* Playback & System */}
      <Text color={textColorSecondary} fontSize="md" fontWeight="600" mb="8px">
        {t('admin.dashboard.playbackHours')}
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2, '2xl': 4 }} gap="20px" mb="20px">
        <MiniStatistics
          startContent={<IconBox icon={MdPlayCircle} bg="brand.500" />}
          name={t('admin.dashboard.playbackToday')}
          value={data?.playbackHoursToday?.toFixed(1) ?? '0'}
        />
        <MiniStatistics
          startContent={<IconBox icon={MdDateRange} bg="brand.500" />}
          name={t('admin.dashboard.playbackWeek')}
          value={data?.playbackHoursWeek?.toFixed(1) ?? '0'}
        />
        <MiniStatistics
          startContent={<IconBox icon={MdLibraryMusic} bg="brand.500" />}
          name={t('admin.dashboard.libraryTracks')}
          value={data?.libraryTrackCount ?? 0}
        />
        <MiniStatistics
          startContent={
            <IconBox
              icon={MdWarning}
              bg={data?.errorRate != null ? (data.errorRate > 0.01 ? 'red.500' : 'green.500') : 'gray.400'}
            />
          }
          name={t('admin.dashboard.errorRate')}
          value={errorRateValue}
        />
      </SimpleGrid>

      <TrendCharts />
    </Box>
  );
}
